import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VoordeurOptionsPanel from '../components/VoordeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  profileSystems,
  glassTextures, 
  glazingTypes, 
  colors,
  woodJointOptions,
  PRICING 
} from '../data/mockData';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  applyDesignTypeChange,
  applyDoorTypeChange,
  VOORDEUR_DOOR_LEAF_MM,
  VOORDEUR_TOP_LIGHT_HEIGHT_MM,
  VOORDEUR_SIDE_PANEL_MM,
  DOOR_NO_PANEL_MAX_WIDTH,
  DOOR_MAX_TOTAL_WIDTH,
  DOOR_MAX_DOOR_LEAF_WIDTH,
  DOOR_MIN_DOOR_HEIGHT_MM,
  DOOR_NORMAL_MAX_HEIGHT,
  DOOR_MAX_DOOR_HEIGHT,
  DOOR_MIN_OVERHEAD_HEIGHT,
  DOOR_MAX_TOTAL_HEIGHT,
  getDoorMinTotalWidth,
  enforceDoorWidthBounds,
  enforceDoorHeightBounds,
  syncSidePanelPartsForWidth,
} from '../lib/voordeurConfigHelpers';
import {
  applyDoorLayoutFieldChange,
} from '../lib/partWidthDistribution';
import {
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import {
  applyPersistedConfiguratorPreferences,
  trackSharedConfiguratorChange,
} from '../lib/configuratorGlazing';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const VOORDEUR_MIN_SIDE_PANEL_WIDTH = VOORDEUR_SIDE_PANEL_MM;
const VOORDEUR_MIN_DOOR_LEAF_WIDTH = 700;
const VOORDEUR_MIN_TOTAL_HEIGHT = DOOR_MIN_DOOR_HEIGHT_MM;

const DEFAULT_VOORDEUR_CONFIG = {
  productType: 'voordeur',
  width: 1000,
  height: 2200,
  doorStyle: 'style-1',
  designType: 'no-panel',
  doorType: 'normal',
  numberOfParts: 3,
  thresholdHeight: '30mm',
  handlePosition: 'left',
  parapet: 'rvs-bar',
  security: 'skg-2',
  profileSystem: 'schuco',
  glassTexture: 'helder-glas',
  glazingType: 'double',
  woodJoint: 'joint-45',
  interiorColor: 'ral-9016',
  exteriorColor: 'anthracite',
  draaideelColor: 'anthracite',
  quantity: 1,
  overheadParts: 1,
  overheadPartWindowTypes: ['fixed-sash'],
  ventilatorOverheadParts: [false],
  overheadHeight: undefined,
  leftPanelWidth: undefined,
  rightPanelWidth: undefined,
  leftPanelWindowType: 'fixed-sash',
  rightPanelWindowType: 'fixed-sash',
  leftPanelParts: 1,
  rightPanelParts: 1,
  leftPanelPartWidths: undefined,
  rightPanelPartWidths: undefined,
  leftPanelPartWindowTypes: ['fixed-sash'],
  rightPanelPartWindowTypes: ['fixed-sash'],
  leftPanelVentilator: false,
  rightPanelVentilator: false,
  leftPanelColor: 'ral-7016',
  rightPanelColor: 'ral-7016',
  leftPanelParapet: false,
  rightPanelParapet: false,
  leftPanelParapetHeight: 250,
  rightPanelParapetHeight: 250,
  leftPanelParapetColor: undefined,
  rightPanelParapetColor: undefined,
  leftPanelParapetPanelType: undefined,
  rightPanelParapetPanelType: undefined,
  leftPanelExtendDividersIntoParapet: false,
  rightPanelExtendDividersIntoParapet: false
};

const Voordeur = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);
  
  const buildDefaultConfig = () =>
    withDefaultAdditionalItems(
      applyPersistedConfiguratorPreferences(DEFAULT_VOORDEUR_CONFIG, DOOR_COLOR_FIELDS)
    );

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) => withDefaultAdditionalItems(loaded)
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    trackSharedConfiguratorChange(field, value);
    markDirty();
    setConfig((prev) => {
      if (field === 'designType') {
        return enforceDoorWidthBounds({ ...prev, ...applyDesignTypeChange(prev, value) });
      }
      if (field === 'leftPanelParts') {
        return {
          ...prev,
          ...syncSidePanelPartsForWidth(prev, 'left', prev.leftPanelWidth, value),
        };
      }
      if (field === 'rightPanelParts') {
        return {
          ...prev,
          ...syncSidePanelPartsForWidth(prev, 'right', prev.rightPanelWidth, value),
        };
      }
      if (field === 'doorType') {
        return { ...prev, ...applyDoorTypeChange(prev, value) };
      }
      if (field === 'overheadParts') {
        const parts = Math.max(1, Number(value) || 1);
        const types = [...(prev.overheadPartWindowTypes || [])];
        const vents = [...(prev.ventilatorOverheadParts || [])];
        while (types.length < parts) types.push('fixed-sash');
        while (vents.length < parts) vents.push(false);
        return {
          ...prev,
          overheadParts: parts,
          overheadPartWindowTypes: types.slice(0, parts),
          ventilatorOverheadParts: vents.slice(0, parts),
        };
      }
      if (field === 'doorStyle') {
        const next = { ...prev, doorStyle: value };
        if (value === 'style-6') {
          next.style6Layout = Number(prev.numberOfParts) === 4 ? 'k' : 'm';
        }
        return next;
      }
      if (field === 'numberOfParts') {
        const parts = Number(value);
        if (prev.doorStyle === 'style-6') {
          return {
            ...prev,
            numberOfParts: parts,
            style6Layout: parts === 4 ? 'k' : 'm',
          };
        }
        return { ...prev, numberOfParts: parts };
      }

      if (field === 'width') {
        const numericWidth = Math.round(Number(value) || prev.width);
        const currentDesign = prev.designType || 'no-panel';
        if (
          numericWidth > DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign === 'no-panel'
        ) {
          return enforceDoorWidthBounds({
            ...prev,
            designType: 'left-panel',
            width: numericWidth,
            leftPanelWidth: Math.max(
              VOORDEUR_MIN_SIDE_PANEL_WIDTH,
              numericWidth - VOORDEUR_DOOR_LEAF_MM
            ),
            rightPanelWidth: undefined,
          });
        }
        if (
          numericWidth <= DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign !== 'no-panel'
        ) {
          return {
            ...prev,
            designType: 'no-panel',
            width: numericWidth,
            leftPanelWidth: undefined,
            rightPanelWidth: undefined,
          };
        }
      }

      if (field === 'height') {
        const numericHeight = Math.round(Number(value) || prev.height);
        if (numericHeight > DOOR_NORMAL_MAX_HEIGHT) {
          return enforceDoorHeightBounds({
            ...prev,
            doorType: 'top-light',
            height: numericHeight,
            overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
          });
        }
        if (prev.doorType === 'top-light') {
          return {
            ...prev,
            doorType: 'normal',
            height: numericHeight,
          };
        }
        return { ...prev, height: numericHeight };
      }

      if (field === 'overheadHeight') {
        return enforceDoorHeightBounds({
          ...prev,
          overheadHeight: Math.round(Number(value) || prev.overheadHeight || VOORDEUR_TOP_LIGHT_HEIGHT_MM),
        });
      }

      const layoutUpdate = applyDoorLayoutFieldChange(prev, field, value);
      if (layoutUpdate) {
        return enforceDoorWidthBounds({ ...prev, ...layoutUpdate });
      }

      return { ...prev, [field]: value };
    });
  };

  useEffect(() => {
    if (location.pathname !== '/voordeur') {
      navigate('/configurator-select', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <ConfiguratorShell
      title={t('configurator.frontDoorConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <VoordeurOptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={
        <DoorDrawingBoard
          width={config.width}
          height={config.height}
          doorStyle={config.doorStyle}
          designType={config.designType}
          doorType={config.doorType}
          numberOfParts={config.numberOfParts ?? 3}
          onNumberOfPartsChange={(value) => handleConfigChange('numberOfParts', value)}
          handlePosition={config.handlePosition}
          interiorColor={config.interiorColor}
          exteriorColor={config.exteriorColor}
          draaideelColor={config.draaideelColor || 'anthracite'}
          glassTexture={config.glassTexture}
          woodJoint={config.woodJoint || 'joint-45'}
          parapet={config.parapet}
          overheadPartWindowTypes={config.overheadPartWindowTypes}
          overheadHeight={config.overheadHeight}
          leftPanelWidth={config.leftPanelWidth}
          rightPanelWidth={config.rightPanelWidth}
          leftPanelWindowType={config.leftPanelWindowType}
          rightPanelWindowType={config.rightPanelWindowType}
          leftPanelColor={config.leftPanelColor}
          rightPanelColor={config.rightPanelColor}
          onLeftPanelColorChange={(value) => handleConfigChange('leftPanelColor', value)}
          onRightPanelColorChange={(value) => handleConfigChange('rightPanelColor', value)}
          onOverheadPartWindowTypeChange={(index, value) => {
            markDirty();
            setConfig((prev) => {
              const newTypes = [...(prev.overheadPartWindowTypes || ['fixed-sash'])];
              newTypes[index] = value;
              const next = { ...prev, overheadPartWindowTypes: newTypes };
              if (value !== 'fixed-sash') {
                const vents = [...(prev.ventilatorOverheadParts || [])];
                vents[index] = false;
                next.ventilatorOverheadParts = vents;
              }
              return next;
            });
          }}
          ventilatorOverheadParts={config.ventilatorOverheadParts || [false]}
          onVentilatorOverheadPartsChange={(value) => handleConfigChange('ventilatorOverheadParts', value)}
          onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
          onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
          onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
          onDoorSectionWidthChange={(value) => handleConfigChange('doorSectionWidth', value)}
          onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
          onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
          leftPanelVentilator={config.leftPanelVentilator}
          rightPanelVentilator={config.rightPanelVentilator}
          onLeftPanelVentilatorChange={(value) => handleConfigChange('leftPanelVentilator', value)}
          onRightPanelVentilatorChange={(value) => handleConfigChange('rightPanelVentilator', value)}
          leftPanelParts={config.leftPanelParts || 1}
          rightPanelParts={config.rightPanelParts || 1}
          leftPanelPartWidths={config.leftPanelPartWidths}
          rightPanelPartWidths={config.rightPanelPartWidths}
          onLeftPanelPartsChange={(value) => handleConfigChange('leftPanelParts', value)}
          onRightPanelPartsChange={(value) => handleConfigChange('rightPanelParts', value)}
          onLeftPanelPartWidthsChange={(value) => handleConfigChange('leftPanelPartWidths', value)}
          onRightPanelPartWidthsChange={(value) => handleConfigChange('rightPanelPartWidths', value)}
          leftPanelPartWindowTypes={config.leftPanelPartWindowTypes}
          rightPanelPartWindowTypes={config.rightPanelPartWindowTypes}
          onLeftPanelPartWindowTypesChange={(value) => handleConfigChange('leftPanelPartWindowTypes', value)}
          onRightPanelPartWindowTypesChange={(value) => handleConfigChange('rightPanelPartWindowTypes', value)}
          leftPanelExtendDividersIntoParapet={config.leftPanelExtendDividersIntoParapet}
          rightPanelExtendDividersIntoParapet={config.rightPanelExtendDividersIntoParapet}
          onLeftPanelExtendDividersIntoParapetChange={(value) => handleConfigChange('leftPanelExtendDividersIntoParapet', value)}
          onRightPanelExtendDividersIntoParapetChange={(value) => handleConfigChange('rightPanelExtendDividersIntoParapet', value)}
          leftPanelParapet={config.leftPanelParapet}
          rightPanelParapet={config.rightPanelParapet}
          leftPanelParapetHeight={config.leftPanelParapetHeight}
          rightPanelParapetHeight={config.rightPanelParapetHeight}
          leftPanelParapetColor={config.leftPanelParapetColor}
          rightPanelParapetColor={config.rightPanelParapetColor}
          leftPanelParapetPanelType={config.leftPanelParapetPanelType}
          rightPanelParapetPanelType={config.rightPanelParapetPanelType}
          onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
          onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
          onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
          onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
          onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
          onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
          onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
          onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
          onWidthChange={(value) => handleConfigChange('width', value)}
          onHeightChange={(value) => handleConfigChange('height', value)}
          minWidth={getDoorMinTotalWidth(config.designType)}
          maxWidth={DOOR_MAX_TOTAL_WIDTH}
          maxDoorLeafWidth={DOOR_MAX_DOOR_LEAF_WIDTH}
          minDoorLeafWidth={VOORDEUR_MIN_DOOR_LEAF_WIDTH}
          minSidePanelWidth={VOORDEUR_MIN_SIDE_PANEL_WIDTH}
          minHeight={VOORDEUR_MIN_TOTAL_HEIGHT}
          maxHeight={DOOR_MAX_TOTAL_HEIGHT}
          maxDoorHeight={DOOR_MAX_DOOR_HEIGHT}
          minDoorHeight={DOOR_MIN_DOOR_HEIGHT_MM}
          minOverheadHeight={DOOR_MIN_OVERHEAD_HEIGHT}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="voordeur"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig, options) => {
            if (options?.resetToDefault) {
              handleReset();
              return;
            }
            resetBaseline(savedConfig || config);
          }}
        />
      }
    />
  );
};

export default Voordeur;
